import { motion } from "framer-motion";
import { BookOpen, ThumbsUp, Share2 } from "lucide-react";
import { getMediaUrl } from "utils/getMediaUrl";
import { expertImages } from "dataClient/expert";
import CardActionButtons from "components/CardActionButtons";
import HighlightText from "components/HighlightText";

function formatDate(value) {
  if (!value) return "";
  try {
    return new Intl.DateTimeFormat("fr-FR", {
      day: "numeric",
      month: "short",
      year: "numeric",
    }).format(new Date(value));
  } catch {
    return value;
  }
}

function initials(name = "") {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() || "")
    .join("");
}

function expertDisplayName(expert) {
  if (!expert) return "";
  const title =
    expert.title === "DOC" ? "Dr" : expert.title === "PROF" ? "Pr" : "";
  const compositeName = `${expert.first_name || ""} ${expert.last_name || ""}`.trim();
  const rawName =
    expert.full_name ||
    expert.name ||
    expert.expert_name ||
    expert.display_name ||
    compositeName;
  const cleanName = (rawName || "").trim();
  return [title, cleanName].filter(Boolean).join(" ");
}

/** Resolved portrait, falling back to the gender placeholder used by ExpertV2Card. */
function expertPortrait(expert) {
  if (!expert) return { src: null, isDefault: false };
  const src = expert.profile_image || expert.picture || expert.avatar;
  if (src) {
    const resolved =
      typeof src === "string" && src.startsWith("http")
        ? src
        : getMediaUrl(src) || src;
    if (resolved) return { src: resolved, isDefault: false };
  }
  return {
    src: expertImages[expert.gender === "M" ? 11 : 17],
    isDefault: true,
  };
}

function veilleExcerpt(item) {
  return (
    item?.retenir ||
    item?.resume ||
    item?.description ||
    "Synthèse claire pour rester à jour."
  );
}

function avisExcerpt(item) {
  return (
    item?.retenir ||
    item?.resume ||
    item?.description ||
    "Point de vue d’expert pour éclairer une décision."
  );
}

function isCriticItem(item) {
  return Boolean(item?.is_critic);
}

export function CriticAlert() {
  return (
    <span className="v2-critic-alert" title="Critique" aria-label="Critique">
      <span className="v2-critic-alert-core" />
    </span>
  );
}

/** Grid / featured card for /veille (portrait layout when expert). */
export function VeilleV2Card({
  item,
  featured = false,
  onOpen,
  onFavoriteChange,
  isOnFavoritesPage = false,
  highlightQuery = "",
}) {
  const category = item?.Categorie?.name || item?.category?.name || "Actualité";
  const expertName = expertDisplayName(item?.expert);
  const { src: avatar, isDefault: avatarIsDefault } = expertPortrait(
    item?.expert,
  );
  const hasExpert = Boolean(item?.expert);
  const unread = item?.isreaded === false;
  const stats = item?.stats || {};
  const isFeaturedExpert = featured && hasExpert;
  const critic = isCriticItem(item);
  const cardClassName = hasExpert
    ? `v2-card v2-veille-expert-card${isFeaturedExpert ? " v2-veille-expert-horizontal" : ""}${critic ? " v2-card-critic" : ""}`
    : `v2-card${featured ? " v2-card-featured" : ""}${critic ? " v2-card-critic" : ""}`;

  return (
    <motion.article
      layout
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 8 }}
      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
      className={cardClassName}
      onClick={() => onOpen(item.id)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onOpen(item.id);
      }}
    >
      {hasExpert ? (
        <>
          <div className={`v2-veille-expert-visual${avatarIsDefault || !avatar ? " is-default" : ""}`}>
            {avatar ? (
              <img
                className={`v2-avis-portrait${avatarIsDefault ? " is-default" : ""}`}
                src={avatar}
                alt={expertName}
              />
            ) : (
              <div className="v2-avis-portrait-fallback">
                {initials(expertName || category)}
              </div>
            )}
            {!isFeaturedExpert && (
              <div className="v2-avis-visual-top">
                {critic ? <CriticAlert /> : null}
                <span className="v2-pill v2-avis-pill">
                  <HighlightText text={category} query={highlightQuery} />
                </span>
                {unread ? <span className="v2-badge-new">Nouveau</span> : null}
              </div>
            )}
            {!isFeaturedExpert && (
              <CardActionButtons
                id={item.id}
                favoriteType="news"
                editType="note"
                initialIsFavorite={Boolean(item?.IsFavorite ?? item?.is_favorite)}
                onFavoriteChange={onFavoriteChange}
                isOnFavoritesPage={isOnFavoritesPage}
                size="small"
                className="v2-card-actions"
              />
            )}
          </div>

          <div className="v2-avis-content v2-veille-expert-content">
            {isFeaturedExpert ? (
              <>
                <div className="v2-veille-featured-top">
                  <div className="v2-card-top-left">
                    {critic ? <CriticAlert /> : null}
                    <span className="v2-pill v2-avis-pill">
                      <HighlightText text={category} query={highlightQuery} />
                    </span>
                    {unread ? <span className="v2-badge-new">Nouveau</span> : null}
                  </div>
                  <CardActionButtons
                    id={item.id}
                    favoriteType="news"
                    editType="note"
                    initialIsFavorite={Boolean(item?.IsFavorite ?? item?.is_favorite)}
                    onFavoriteChange={onFavoriteChange}
                    isOnFavoritesPage={isOnFavoritesPage}
                    size="small"
                    className="v2-card-actions"
                  />
                </div>

                <h3 className="v2-card-title">
                  <HighlightText text={item?.title} query={highlightQuery} />
                </h3>

                <p className="v2-card-excerpt">
                  <HighlightText text={veilleExcerpt(item)} query={highlightQuery} />
                </p>

                <div className="v2-card-foot v2-veille-featured-foot">
                  <div className="v2-avis-byline">
                    <p className="v2-author-name">
                      <HighlightText text={expertName} query={highlightQuery} />
                    </p>
                    <span aria-hidden="true">·</span>
                    <p className="v2-author-date">
                      {formatDate(item?.lastUpdateDate || item?.updated_at)}
                    </p>
                  </div>
                  <div className="v2-stats">
                    {(stats.view_count ?? stats.views) != null && (
                      <span className="v2-stat">
                        <BookOpen size={14} />
                        {stats.view_count ?? stats.views}
                      </span>
                    )}
                    {(stats.like_count ?? stats.likes) != null && (
                      <span className="v2-stat">
                        <ThumbsUp size={14} />
                        {stats.like_count ?? stats.likes}
                      </span>
                    )}
                    {(stats.share_count ?? stats.shares) != null && (
                      <span className="v2-stat">
                        <Share2 size={14} />
                        {stats.share_count ?? stats.shares}
                      </span>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="v2-avis-byline">
                  <p className="v2-author-name">
                    <HighlightText text={expertName} query={highlightQuery} />
                  </p>
                  <span aria-hidden="true">·</span>
                  <p className="v2-author-date">
                    {formatDate(item?.lastUpdateDate || item?.updated_at)}
                  </p>
                </div>

                <h3 className="v2-card-title">
                  <HighlightText text={item?.title} query={highlightQuery} />
                </h3>

                <p className="v2-card-excerpt">
                  <HighlightText text={veilleExcerpt(item)} query={highlightQuery} />
                </p>

                <div className="v2-avis-stats">
                  <div className="v2-stats">
                    {(stats.view_count ?? stats.views) != null && (
                      <span className="v2-stat">
                        <BookOpen size={14} />
                        {stats.view_count ?? stats.views}
                      </span>
                    )}
                    {(stats.like_count ?? stats.likes) != null && (
                      <span className="v2-stat">
                        <ThumbsUp size={14} />
                        {stats.like_count ?? stats.likes}
                      </span>
                    )}
                    {(stats.share_count ?? stats.shares) != null && (
                      <span className="v2-stat">
                        <Share2 size={14} />
                        {stats.share_count ?? stats.shares}
                      </span>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </>
      ) : (
        <>
          <div className="v2-card-top">
            <div className="v2-card-top-left">
              {critic ? <CriticAlert /> : null}
              <span className={`v2-pill${featured ? " v2-pill-accent" : ""}`}>
                <HighlightText text={category} query={highlightQuery} />
              </span>
              {unread ? <span className="v2-badge-new">Nouveau</span> : null}
            </div>
            <CardActionButtons
              id={item.id}
              favoriteType="news"
              editType="note"
              initialIsFavorite={Boolean(item?.IsFavorite ?? item?.is_favorite)}
              onFavoriteChange={onFavoriteChange}
              isOnFavoritesPage={isOnFavoritesPage}
              size="small"
              className="v2-card-actions"
            />
          </div>

          <h3 className="v2-card-title">
            <HighlightText text={item?.title} query={highlightQuery} />
          </h3>

          {(featured || veilleExcerpt(item)) && (
            <p className="v2-card-excerpt">
              <HighlightText text={veilleExcerpt(item)} query={highlightQuery} />
            </p>
          )}

          <div className="v2-card-foot">
            <div className="v2-author">
              <p className="v2-author-date">
                {formatDate(item?.lastUpdateDate || item?.updated_at)}
              </p>
            </div>
            <div className="v2-stats">
              {(stats.view_count ?? stats.views) != null && (
                <span className="v2-stat">
                  <BookOpen size={14} />
                  {stats.view_count ?? stats.views}
                </span>
              )}
              {(stats.like_count ?? stats.likes) != null && (
                <span className="v2-stat">
                  <ThumbsUp size={14} />
                  {stats.like_count ?? stats.likes}
                </span>
              )}
              {(stats.share_count ?? stats.shares) != null && (
                <span className="v2-stat">
                  <Share2 size={14} />
                  {stats.share_count ?? stats.shares}
                </span>
              )}
            </div>
          </div>
        </>
      )}
    </motion.article>
  );
}

/** Same card as /avis list */
export function AvisV2Card({
  item,
  onOpen,
  onFavoriteChange,
  isOnFavoritesPage = false,
  highlightQuery = "",
}) {
  const category = item?.category?.name || item?.Categorie?.name || "Avis";
  const expertName = expertDisplayName(item?.expert);
  const { src: avatar, isDefault: avatarIsDefault } = expertPortrait(
    item?.expert,
  );
  const unread = item?.is_readed === false;
  const stats = item?.stats || {};
  const critic = isCriticItem(item);

  return (
    <motion.article
      layout
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 8 }}
      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
      className={`v2-card v2-avis-card${critic ? " v2-card-critic" : ""}`}
      onClick={() => onOpen(item.id)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onOpen(item.id);
      }}
    >
      <div className={`v2-avis-visual${avatarIsDefault || !avatar ? " is-default" : ""}`}>
        {avatar ? (
          <img
            className={`v2-avis-portrait${avatarIsDefault ? " is-default" : ""}`}
            src={avatar}
            alt={expertName}
          />
        ) : (
          <div className="v2-avis-portrait-fallback">
            {initials(expertName || category)}
          </div>
        )}
        <div className="v2-avis-visual-top">
          {critic ? <CriticAlert /> : null}
          <span className="v2-pill v2-avis-pill">
            <HighlightText text={category} query={highlightQuery} />
          </span>
          {unread ? <span className="v2-badge-new">Nouveau</span> : null}
        </div>
        <CardActionButtons
          id={item.id}
          favoriteType="expertopinion"
          editType="avis"
          initialIsFavorite={Boolean(item?.is_favorite ?? item?.IsFavorite)}
          onFavoriteChange={onFavoriteChange}
          isOnFavoritesPage={isOnFavoritesPage}
          size="small"
          className="v2-card-actions"
        />
      </div>

      <div className="v2-avis-content">
        <div className="v2-avis-byline">
          <p className="v2-author-name">
            <HighlightText
              text={expertName || "Expert"}
              query={highlightQuery}
            />
          </p>
          <span aria-hidden="true">·</span>
          <p className="v2-author-date">
            {formatDate(item?.lastUpdateDate || item?.updated_at)}
          </p>
        </div>

        <h3 className="v2-card-title">
          <HighlightText text={item?.title} query={highlightQuery} />
        </h3>

        <p className="v2-card-excerpt">
          <HighlightText text={avisExcerpt(item)} query={highlightQuery} />
        </p>

        <div className="v2-avis-stats">
          <div className="v2-stats">
            {(stats.views ?? stats.view_count) != null && (
              <span className="v2-stat">
                <BookOpen size={14} />
                {stats.views ?? stats.view_count}
              </span>
            )}
            {(stats.likes ?? stats.like_count) != null && (
              <span className="v2-stat">
                <ThumbsUp size={14} />
                {stats.likes ?? stats.like_count}
              </span>
            )}
            {(stats.shares ?? stats.share_count) != null && (
              <span className="v2-stat">
                <Share2 size={14} />
                {stats.shares ?? stats.share_count}
              </span>
            )}
          </div>
        </div>
      </div>
    </motion.article>
  );
}
